import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, X, FileText, Image as ImageIcon, CheckCircle, AlertCircle, Calculator, Type, Grid, List, ChevronDown, ShoppingCart } from 'lucide-react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import ProductCard from '../components/ProductCard';
import ConfigurationForm from '../components/ConfigurationForm';
import ConfigurationPreview from '../components/config/ConfigurationPreview';
import axios from 'axios';

const DevisMarquageFlocagePage = () => {
  const navigate = useNavigate();
  
  // ========== États pour la section formulaire de contact ==========
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // ========== États pour la section devis immédiat ==========
  const [activeTab, setActiveTab] = useState('calculator'); // 'form' ou 'calculator'
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('relevance');
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedPatterns, setSelectedPatterns] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collections, setCollections] = useState([]);
  const [realProducts, setRealProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // États pour la sélection du produit et configuration
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [configData, setConfigData] = useState(null);
  const [marquageText, setMarquageText] = useState('RESTAURANT LE SOLEIL');
  const [marquageColor, setMarquageColor] = useState('white'); // 'white' ou 'black'
  const [marquageCalculation, setMarquageCalculation] = useState(null);
  const [marquageLoading, setMarquageLoading] = useState(false);
  const [marquageError, setMarquageError] = useState(null);
  const [filmPricingExists, setFilmPricingExists] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];

  // Charger les collections
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/collections/`);
        const collectionsData = response.data || [];
        setCollections(collectionsData);
        if (collectionsData.length > 0 && !selectedCollection) {
          setSelectedCollection(collectionsData[0].name);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des collections:', error);
        setCollections([]);
      }
    };
    fetchCollections();
  }, [BACKEND_URL, selectedCollection]);

  // Charger les produits
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/products/`);
        const adaptedProducts = response.data.map(product => ({
          id: product.id,
          name: product.ref_frs || product.code1 || product.full_code || 'Produit',
          collection: product.gamme || 'N/A',
          brand: product.supplier || 'N/A',
          price: 0,
          image: product.image || null,
          slug: product.slug || product.id,
          thumbnail: product.thumbnail || product.image || null,
          supplier: product.supplier,
          category: product.category,
          code1: product.code1,
          ref_frs: product.ref_frs,
          width: product.width,
          ral: product.ral || null // Code RAL pour la couleur
        }));
        setRealProducts(adaptedProducts);
        
        // Sélectionner automatiquement le produit "3914 ROUGE" par défaut
        const defaultProduct = adaptedProducts.find(p => 
          p.code1?.includes('3914') || p.ref_frs?.includes('3914')
        );
        if (defaultProduct && !selectedProduct) {
          setSelectedProduct(defaultProduct);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des produits:', error);
        setLoading(false);
      }
    };
    fetchProducts();
  }, [BACKEND_URL]);

  // Vérifier si le prix du film existe
  useEffect(() => {
    const checkFilmPricing = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/marquage/film-pricing`);
        setFilmPricingExists(response.data.exists);
      } catch (error) {
        console.error('Erreur vérification prix film:', error);
        setFilmPricingExists(false);
      }
    };
    checkFilmPricing();
  }, [BACKEND_URL]);

  // Filtrer les produits
  const filteredProducts = useMemo(() => {
    if (realProducts.length === 0) return [];
    let filtered = [...realProducts];

    if (selectedCollection) {
      filtered = filtered.filter(p => p.collection === selectedCollection);
    }
    if (selectedBrands.length > 0) {
      filtered = filtered.filter(p => selectedBrands.includes(p.brand));
    }
    if (selectedColors.length > 0) {
      filtered = filtered.filter(p => selectedColors.includes(p.color));
    }
    if (selectedPatterns.length > 0) {
      filtered = filtered.filter(p => selectedPatterns.includes(p.pattern));
    }
    if (searchQuery) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    switch(sortBy) {
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        break;
    }

    return filtered;
  }, [selectedCollection, selectedBrands, selectedColors, selectedPatterns, searchQuery, sortBy, realProducts]);

  // Calculer le marquage quand le texte ou la configuration change
  useEffect(() => {
    const calculateMarquage = async () => {
      if (!marquageText || !configData?.hauteurLambrequin) {
        setMarquageCalculation(null);
        return;
      }

      setMarquageLoading(true);
      setMarquageError(null);

      try {
        const response = await axios.post(`${BACKEND_URL}/api/marquage/calculate`, {
          text: marquageText,
          hauteur_lambrequin: configData.hauteurLambrequin
        });
        setMarquageCalculation(response.data);
      } catch (error) {
        console.error('Erreur calcul marquage:', error);
        setMarquageError(error.response?.data?.detail || 'Erreur lors du calcul du marquage');
        setMarquageCalculation(null);
      } finally {
        setMarquageLoading(false);
      }
    };

    const debounce = setTimeout(calculateMarquage, 300);
    return () => clearTimeout(debounce);
  }, [marquageText, configData?.hauteurLambrequin, BACKEND_URL]);

  // ========== Handlers du formulaire de contact ==========
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = [];
    const fileErrors = [];

    selectedFiles.forEach(file => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        fileErrors.push(`${file.name}: Type de fichier non autorisé`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        fileErrors.push(`${file.name}: Fichier trop volumineux (max 10 MB)`);
        return;
      }
      validFiles.push(file);
    });

    if (fileErrors.length > 0) {
      setErrors(prev => ({ ...prev, files: fileErrors.join(', ') }));
    } else {
      setErrors(prev => ({ ...prev, files: null }));
    }
    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Le nom est obligatoire';
    if (!formData.phone.trim()) newErrors.phone = 'Le téléphone est obligatoire';
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est obligatoire';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }
    if (!formData.message.trim()) newErrors.message = 'Veuillez décrire votre demande';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);
      files.forEach(file => formDataToSend.append('files', file));

      const response = await fetch(`${BACKEND_URL}/api/quote-request/`, {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();
      if (response.ok) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
        setErrors({ submit: data.detail || 'Une erreur est survenue' });
      }
    } catch (error) {
      console.error('Error submitting quote request:', error);
      setSubmitStatus('error');
      setErrors({ submit: 'Erreur de connexion au serveur' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) {
      return <ImageIcon className="w-5 h-5 text-blue-500" />;
    }
    return <FileText className="w-5 h-5 text-red-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Handler pour la sélection d'un produit
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setConfigData(null);
    // On garde le texte de marquage (ou le texte par défaut)
    if (!marquageText) {
      setMarquageText('RESTAURANT LE SOLEIL');
    }
    setMarquageCalculation(null);
  };

  // Handler pour les changements de configuration
  const handleConfigChange = useCallback((newConfig) => {
    setConfigData(newConfig);
  }, []);

  // Calcul du prix total
  const prixTotal = useMemo(() => {
    const prixLambrequin = configData?.totalPrice || 0;
    const prixMarquage = marquageCalculation?.prix_film || 0;
    return prixLambrequin + prixMarquage;
  }, [configData?.totalPrice, marquageCalculation?.prix_film]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <button onClick={() => navigate('/')} className="hover:text-celadon transition-colors">
              Accueil
            </button>
            <span>/</span>
            <span className="text-gray-900 font-medium">Marquage/Flocage de lambrequin - Votre devis immédiat</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('calculator')}
              className={`py-4 px-6 font-medium border-b-2 transition-colors ${
                activeTab === 'calculator'
                  ? 'border-celadon text-celadon'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Calculator className="inline-block w-5 h-5 mr-2" />
              Devis Immédiat
            </button>
            <button
              onClick={() => setActiveTab('form')}
              className={`py-4 px-6 font-medium border-b-2 transition-colors ${
                activeTab === 'form'
                  ? 'border-celadon text-celadon'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="inline-block w-5 h-5 mr-2" />
              Demande Personnalisée
            </button>
          </div>
        </div>
      </div>

      <main className="flex-grow">
        {activeTab === 'calculator' ? (
          /* ========== SECTION DEVIS IMMÉDIAT ========== */
          <div className="max-w-7xl mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Marquage/Flocage de lambrequin - Votre devis immédiat</h1>
            <p className="text-gray-600 mb-8">
              Sélectionnez votre toile, configurez votre lambrequin et entrez le texte à marquer pour obtenir un devis instantané.
            </p>

            {!filmPricingExists && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                <div>
                  <p className="text-yellow-800 font-medium">Configuration requise</p>
                  <p className="text-yellow-700 text-sm">
                    La catégorie &quot;Film de marquage&quot; n&apos;existe pas dans les prix. 
                    Veuillez l&apos;ajouter dans <strong>Admin &gt; Gestion des Prix</strong> pour activer le calcul du marquage.
                  </p>
                </div>
              </div>
            )}

            {!selectedProduct ? (
              /* Étape 1: Sélection du produit */
              <div className="flex gap-8">
                <Sidebar 
                  selectedBrands={selectedBrands}
                  setSelectedBrands={setSelectedBrands}
                  selectedColors={selectedColors}
                  setSelectedColors={setSelectedColors}
                  selectedPatterns={selectedPatterns}
                  setSelectedPatterns={setSelectedPatterns}
                  selectedCollection={selectedCollection}
                  setSelectedCollection={setSelectedCollection}
                />

                <div className="flex-1">
                  <div className="bg-white rounded-lg p-4 mb-6 shadow-sm">
                    <h2 className="text-lg font-semibold text-gray-800 mb-2">Étape 1 : Sélectionnez votre toile</h2>
                    <p className="text-sm text-gray-500">Choisissez la toile pour votre lambrequin avec marquage</p>
                  </div>

                  <div className="flex items-center justify-between mb-6 pb-4 border-b">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`p-2 ${viewMode === 'grid' ? 'text-gray-900' : 'text-gray-400'}`}
                      >
                        <Grid size={20} />
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`p-2 ${viewMode === 'list' ? 'text-gray-900' : 'text-gray-400'}`}
                      >
                        <List size={20} />
                      </button>
                      {loading ? (
                        <span className="text-sm text-gray-600">Chargement des produits...</span>
                      ) : (
                        <span className="text-sm text-gray-600">Il y a {filteredProducts.length} produits.</span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600">Trier par:</label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-celadon"
                      >
                        <option value="relevance">Pertinence</option>
                        <option value="name-asc">Nom, A à Z</option>
                        <option value="name-desc">Nom, Z à A</option>
                      </select>
                    </div>
                  </div>

                  {/* Grille de produits cliquables */}
                  <div className={`${viewMode === 'grid' ? 'grid grid-cols-4 gap-6' : 'flex flex-col gap-4'}`}>
                    {filteredProducts.map((product) => (
                      <div
                        key={product.id}
                        onClick={() => handleProductSelect(product)}
                        className="cursor-pointer transform transition-transform hover:scale-105"
                      >
                        <ProductCard 
                          product={product} 
                          viewMode={viewMode} 
                          productType="lambrequin-seul"
                          hideNavigation={true}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              /* Étape 2: Configuration et marquage */
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Colonne gauche: Configuration */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Produit sélectionné */}
                  <div className="bg-white rounded-lg p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-gray-800">Étape 2 : Configurez votre lambrequin</h2>
                      <button
                        onClick={() => setSelectedProduct(null)}
                        className="text-sm text-celadon hover:underline"
                      >
                        ← Changer de toile
                      </button>
                    </div>
                    
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg mb-6">
                      {selectedProduct.thumbnail && (
                        <img 
                          src={selectedProduct.thumbnail} 
                          alt={selectedProduct.name}
                          className="w-20 h-20 object-cover rounded"
                        />
                      )}
                      <div>
                        <h3 className="font-medium text-gray-900">{selectedProduct.name}</h3>
                        <p className="text-sm text-gray-500">{selectedProduct.collection}</p>
                      </div>
                    </div>

                    {/* Section Marquage - Déplacée ici */}
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                      <h3 className="text-md font-semibold text-gray-800 mb-4">
                        <Type className="inline-block w-5 h-5 mr-2 text-celadon" />
                        Texte à marquer
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Entrez le texte à afficher sur votre lambrequin
                          </label>
                          <input
                            type="text"
                            value={marquageText}
                            onChange={(e) => setMarquageText(e.target.value)}
                            placeholder="Ex: RESTAURANT LE SOLEIL"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-celadon focus:border-transparent text-lg"
                          />
                          <p className="text-xs text-gray-500 mt-1">Police utilisée : Arial Bold</p>
                        </div>

                        {/* Sélecteur de couleur */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Couleur du texte
                          </label>
                          <div className="flex gap-4">
                            <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                              marquageColor === 'white' 
                                ? 'border-celadon bg-celadon/10' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}>
                              <input
                                type="radio"
                                name="marquageColor"
                                value="white"
                                checked={marquageColor === 'white'}
                                onChange={(e) => setMarquageColor(e.target.value)}
                                className="sr-only"
                              />
                              <span className="w-6 h-6 rounded border-2 border-gray-300 bg-white shadow-inner"></span>
                              <span className="text-sm font-medium">Blanc</span>
                            </label>
                            <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                              marquageColor === 'black' 
                                ? 'border-celadon bg-celadon/10' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}>
                              <input
                                type="radio"
                                name="marquageColor"
                                value="black"
                                checked={marquageColor === 'black'}
                                onChange={(e) => setMarquageColor(e.target.value)}
                                className="sr-only"
                              />
                              <span className="w-6 h-6 rounded border-2 border-gray-300 bg-black"></span>
                              <span className="text-sm font-medium">Noir</span>
                            </label>
                          </div>
                        </div>

                        {marquageText && configData?.hauteurLambrequin && (
                          <div className="p-4 bg-white rounded-lg border">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Aperçu des dimensions</h4>
                            {marquageLoading ? (
                              <p className="text-sm text-gray-500">Calcul en cours...</p>
                            ) : marquageError ? (
                              <p className="text-sm text-red-500">{marquageError}</p>
                            ) : marquageCalculation ? (
                              <div className="space-y-1 text-sm">
                                <p><span className="text-gray-500">Hauteur du texte :</span> <strong>{marquageCalculation.hauteur_texte_cm} cm</strong> (80% de {configData.hauteurLambrequin} cm)</p>
                                <p><span className="text-gray-500">Largeur du texte :</span> <strong>{marquageCalculation.largeur_texte_cm} cm</strong></p>
                                <p><span className="text-gray-500">Surface du film :</span> <strong>{(marquageCalculation.surface_film_m2 * 10000).toFixed(0)} cm²</strong> ({marquageCalculation.surface_film_m2} m²)</p>
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Aperçu de la configuration - Déplacé ici */}
                    {configData && (
                      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <h3 className="text-md font-semibold text-gray-800 mb-4">Aperçu de la configuration</h3>
                        <ConfigurationPreview
                          largeur={configData.largeur || 400}
                          projection={0}
                          projection2={0}
                          hauteurLambrequin={configData.hauteurLambrequin || 25}
                          lambrequin={configData.lambrequin || 'droit'}
                          isDoublePente={false}
                          isLambrequinSeul={true}
                          isPergola={false}
                          isBriseVue={false}
                          optionsSupp={{
                            oeillets: true,
                            renfortLyre: false,
                            textePersonnalise: false,
                            agrafes: false
                          }}
                          renfortLyreDetails={null}
                          lestageDetails={null}
                          nombreSanglons={0}
                          fabricCalculation={null}
                          marquageText={marquageText}
                          marquageCalculation={marquageCalculation}
                          marquageColor={marquageColor}
                          productImage={selectedProduct.thumbnail || selectedProduct.image}
                          productRal={selectedProduct.ral}
                        />
                      </div>
                    )}

                    {/* Formulaire de configuration */}
                    <ConfigurationForm
                      basePrice={0}
                      onConfigChange={handleConfigChange}
                      productId={selectedProduct.id}
                      isLambrequinSeul={true}
                      productWidth={selectedProduct.width}
                      marquageText={marquageText}
                      marquageCalculation={marquageCalculation}
                      marquageColor={marquageColor}
                      productImage={selectedProduct.thumbnail || selectedProduct.image}
                      productRal={selectedProduct.ral}
                      hidePreview={true}
                    />
                  </div>
                </div>

                {/* Colonne droite: Récapitulatif */}
                <div className="lg:col-span-1">
                  <div className="bg-white rounded-lg p-6 shadow-sm sticky top-24">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Récapitulatif du devis</h2>
                    
                    <div className="space-y-4">
                      {/* Prix du lambrequin */}
                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-gray-600">Lambrequin sur-mesure</span>
                        <span className="font-medium">
                          {configData?.totalPrice ? `${configData.totalPrice.toFixed(2)} €` : '—'}
                        </span>
                      </div>

                      {/* Détails configuration */}
                      {configData && (
                        <div className="text-xs text-gray-500 space-y-1 pb-2 border-b">
                          <p>Toile : {selectedProduct?.name} ({selectedProduct?.collection})</p>
                          <p>Largeur : {configData.largeur || configData.hauteurLambrequin} cm</p>
                          <p>Hauteur : {configData.hauteurLambrequin} cm</p>
                          {configData.lambrequin && <p>Style : {configData.lambrequin}</p>}
                        </div>
                      )}

                      {/* Prix du marquage */}
                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-gray-600">Marquage/Flocage</span>
                        <span className="font-medium">
                          {marquageCalculation ? `${marquageCalculation.prix_film.toFixed(2)} €` : '—'}
                        </span>
                      </div>

                      {/* Détails marquage */}
                      {marquageCalculation && (
                        <div className="text-xs text-gray-500 space-y-1 pb-2 border-b">
                          <p>Texte : &quot;{marquageText}&quot;</p>
                          <p>Police : Arial Bold</p>
                          <p className="flex items-center gap-1">
                            Couleur : 
                            <span className={`inline-block w-3 h-3 rounded border ${marquageColor === 'white' ? 'bg-white border-gray-300' : 'bg-black border-gray-300'}`}></span>
                            {marquageColor === 'white' ? 'Blanc' : 'Noir'}
                          </p>
                          <p>Surface film : {marquageCalculation.surface_film_m2} m²</p>
                          <p>Frais fixes inclus : {marquageCalculation.frais_fixes.toFixed(2)} €</p>
                        </div>
                      )}

                      {/* Total */}
                      <div className="flex justify-between items-center pt-2">
                        <span className="text-lg font-bold text-gray-900">TOTAL TTC</span>
                        <span className="text-2xl font-bold text-celadon">
                          {prixTotal > 0 ? `${prixTotal.toFixed(2)} €` : '—'}
                        </span>
                      </div>

                      {/* Bouton d'action */}
                      <button
                        onClick={() => {
                          // Préremplir le formulaire avec les détails du devis
                          const devisDetails = `
Lambrequin avec Marquage/Flocage

TOILE SÉLECTIONNÉE:
- Référence: ${selectedProduct?.name || 'N/A'}
- Collection: ${selectedProduct?.collection || 'N/A'}

CONFIGURATION:
- Largeur: ${configData?.largeur || 'N/A'} cm
- Hauteur: ${configData?.hauteurLambrequin || 'N/A'} cm
- Style: ${configData?.lambrequin || 'N/A'}

MARQUAGE:
- Texte: "${marquageText}"
- Police: Arial Bold
- Couleur du film: ${marquageColor === 'white' ? 'Blanc' : 'Noir'}
- Dimensions texte: ${marquageCalculation?.hauteur_texte_cm || 'N/A'} x ${marquageCalculation?.largeur_texte_cm || 'N/A'} cm

PRIX:
- Lambrequin: ${configData?.totalPrice?.toFixed(2) || '0.00'} €
- Marquage: ${marquageCalculation?.prix_film?.toFixed(2) || '0.00'} €
- TOTAL: ${prixTotal.toFixed(2)} €
                          `.trim();
                          
                          setFormData(prev => ({ ...prev, message: devisDetails }));
                          setActiveTab('form');
                        }}
                        disabled={!configData?.totalPrice || !marquageCalculation}
                        className="w-full py-3 bg-celadon text-white rounded-lg font-medium hover:bg-celadon-dark transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        <ShoppingCart className="w-5 h-5" />
                        Commander ce devis
                      </button>

                      <p className="text-xs text-center text-gray-500">
                        Un conseiller vous contactera pour finaliser votre commande
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* ========== SECTION FORMULAIRE DE CONTACT ========== */
          <div className="container mx-auto px-4 py-8 max-w-3xl">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Demande de devis personnalisée</h1>
              <p className="text-gray-600 mb-8">
                Remplissez le formulaire ci-dessous pour recevoir un devis personnalisé. Notre équipe vous répondra dans les plus brefs délais.
              </p>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <p className="text-green-800 font-medium">Demande envoyée avec succès !</p>
                      <p className="text-green-700 text-sm mt-1">Vous allez recevoir une copie par email. Notre équipe vous répondra dans les plus brefs délais.</p>
                    </div>
                    <button
                      onClick={() => navigate('/')}
                      className="px-4 py-2 bg-celadon text-white rounded hover:bg-celadon-dark transition-colors text-sm font-medium"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && errors.submit && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                  <div>
                    <p className="text-red-800 font-medium">Erreur</p>
                    <p className="text-red-700 text-sm">{errors.submit}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Nom */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-celadon focus:border-transparent`}
                    placeholder="Jean Dupont"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                </div>

                {/* Téléphone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-celadon focus:border-transparent`}
                    placeholder="06 12 34 56 78"
                  />
                  {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-celadon focus:border-transparent`}
                    placeholder="jean.dupont@example.com"
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Décrivez votre demande <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="10"
                    className={`w-full px-4 py-3 border ${errors.message ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-celadon focus:border-transparent resize-none font-mono text-sm`}
                    placeholder="Décrivez votre projet, dimensions souhaitées, couleurs, etc..."
                  />
                  {errors.message && <p className="mt-1 text-sm text-red-500">{errors.message}</p>}
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Documents (optionnel)
                  </label>
                  <p className="text-sm text-gray-500 mb-3">
                    Vous pouvez joindre des photos, plans ou PDF (max 10 MB par fichier)
                  </p>
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-celadon transition-colors">
                    <input
                      type="file"
                      id="file-upload"
                      multiple
                      accept="image/*,.pdf"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center gap-2">
                      <Upload className="w-10 h-10 text-gray-400" />
                      <span className="text-sm text-gray-600">Cliquez pour ajouter des fichiers</span>
                      <span className="text-xs text-gray-500">Images (JPG, PNG, GIF, WEBP) ou PDF</span>
                    </label>
                  </div>
                  {errors.files && <p className="mt-2 text-sm text-red-500">{errors.files}</p>}

                  {/* File List */}
                  {files.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {getFileIcon(file)}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                              <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                            </div>
                          </div>
                          <button type="button" onClick={() => removeFile(index)} className="p-1 hover:bg-gray-200 rounded transition-colors">
                            <X className="w-5 h-5 text-gray-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-3 bg-celadon text-white rounded-lg hover:bg-celadon-dark transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default DevisMarquageFlocagePage;
